import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';

interface ProfileData {
  first_name: string;
  last_name: string;
  middle_name?: string;
  date_of_birth?: string;
  current_address?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  phone_number?: string;
  additional_emails?: string;
  previous_addresses?: string;
  aliases?: string;
  consent_to_autopilot?: boolean | number;
}

const emptyProfile: ProfileData = {
  first_name: '',
  last_name: '',
  middle_name: '',
  date_of_birth: '',
  current_address: '',
  city: '',
  state: '',
  postal_code: '',
  phone_number: '',
  additional_emails: '',
  previous_addresses: '',
  aliases: '',
  consent_to_autopilot: false
};

const Profile: React.FC = () => {
  const [profile, setProfile] = useState<ProfileData>(emptyProfile);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    api<ProfileData>('/api/profile')
      .then((data) => setProfile({ ...emptyProfile, ...data, consent_to_autopilot: Boolean(data.consent_to_autopilot) }))
      .catch(() => undefined)
      .finally(() => setLoading(false));
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMessage('');
    try {
      await api('/api/profile', { method: 'PATCH', body: JSON.stringify(profile) });
      setMessage('Saved. The indexer can now use this profile for matching and broker request drafts.');
    } catch (err: any) {
      setMessage(err?.message || 'Error saving profile');
    } finally {
      setSaving(false);
    }
  }

  function handleChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value, type } = event.target;
    setProfile((prev) => ({ ...prev, [name]: type === 'checkbox' ? (event.target as HTMLInputElement).checked : value }));
  }

  const requiredDone = Boolean(profile.first_name && profile.last_name && profile.current_address && profile.city && profile.state && profile.postal_code && profile.phone_number && profile.consent_to_autopilot);

  if (loading) return <div className="panelCard">Loading profile...</div>;

  return (
    <div className="pageStack narrowStack">
      <section className="pageIntro">
        <div>
          <span className="eyebrow">My Info</span>
          <h1>Save the identity details used for matching.</h1>
          <p>
            Keep this focused. These fields let the app search broker targets and write accurate opt-out/removal requests.
          </p>
        </div>
        <span className={`badge ${requiredDone ? 'good' : 'warn'}`}>{requiredDone ? 'Ready to index' : 'Needs info'}</span>
      </section>

      <form onSubmit={handleSubmit} className="panelCard profilePanel">
        <div className="formSectionTitle">
          <h2>Required details</h2>
          <p>Most brokers need name, address, phone, and email-level verification.</p>
        </div>

        <div className="formGrid">
          <label>First name<input name="first_name" value={profile.first_name || ''} onChange={handleChange} required /></label>
          <label>Middle<input name="middle_name" value={profile.middle_name || ''} onChange={handleChange} /></label>
          <label>Last name<input name="last_name" value={profile.last_name || ''} onChange={handleChange} required /></label>
          <label>Date of birth<input name="date_of_birth" type="date" value={profile.date_of_birth || ''} onChange={handleChange} /></label>
          <label className="span2">Current street address<input name="current_address" value={profile.current_address || ''} onChange={handleChange} required /></label>
          <label>City<input name="city" value={profile.city || ''} onChange={handleChange} required /></label>
          <label>State<input name="state" value={profile.state || ''} onChange={handleChange} required /></label>
          <label>ZIP<input name="postal_code" value={profile.postal_code || ''} onChange={handleChange} required /></label>
          <label>Phone<input name="phone_number" value={profile.phone_number || ''} onChange={handleChange} required /></label>
        </div>

        <div className="formSectionTitle dividerTop">
          <h2>Optional match helpers</h2>
          <p>These improve matching but are not required to start.</p>
        </div>
        <div className="formGrid">
          <label className="span2">Other emails<textarea name="additional_emails" value={profile.additional_emails || ''} onChange={handleChange} placeholder="One per line or comma-separated" /></label>
          <label className="span2">Previous addresses<textarea name="previous_addresses" value={profile.previous_addresses || ''} onChange={handleChange} placeholder="Older addresses that may appear on people-search sites" /></label>
          <label className="span2">Aliases / alternate spellings<textarea name="aliases" value={profile.aliases || ''} onChange={handleChange} placeholder="Nicknames, old names, common misspellings" /></label>
        </div>

        <label className="consentBox">
          <input type="checkbox" name="consent_to_autopilot" checked={Boolean(profile.consent_to_autopilot)} onChange={handleChange} />
          <span>
            I authorize this private app to generate removal requests from these details and send email-ready broker requests when I click Send.
          </span>
        </label>

        <div className="formActions">
          {message && <span className={`notice ${message.toLowerCase().includes('error') ? 'danger' : 'good'}`}>{message}</span>}
          <button className="primaryButton" disabled={saving}>{saving ? 'Saving...' : 'Save My Info'}</button>
        </div>
      </form>
    </div>
  );
};

export default Profile;
